import React, { useEffect, useMemo, useState } from 'react';
import { ProtectedRoute } from '../../../components/ProtectedRoute';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import {
  OrderStatCard,
  OrdersTable,
  OrdersPagination,
  CreateOrderModal,
} from './components';
import {
  ITEMS_PER_PAGE,
  OrderItem,
  OrderStats,
  statusLabel,
} from './ordersTypes';
import { useOrdersStore } from '../../../store/ordersStore';
import { Order } from '../../../services/ordersApi';
import { formatOrderId } from '../../../utils/formatId';
import styles from './AdminOrders.module.css';

function adapt(o: Order): OrderItem {
  // The backend list endpoint (listOrdersWithAuth) returns `created_at` but
  // not `order_date`; the detail endpoint returns both. Prefer order_date,
  // fall back to created_at, and handle the case where neither is set so
  // the page never crashes on a partial payload.
  const dateSource = o.order_date || o.created_at || '';
  return {
    id: String(o.id),
    orderId: formatOrderId(o.id),
    customer: o.customer_name || `Customer #${o.customer_id}`,
    salesRep: '—',
    date: typeof dateSource === 'string' ? dateSource.slice(0, 10) : '',
    items: o.items?.length ?? 0,
    total: Number(o.total_amount ?? 0),
    margin: 0, // not tracked yet
    status: statusLabel(o.status),
  };
}

const AdminOrders: React.FC = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);

  const items = useOrdersStore((s) => s.items);
  const total = useOrdersStore((s) => s.total);
  const loading = useOrdersStore((s) => s.loading);
  const error = useOrdersStore((s) => s.error);
  const filters = useOrdersStore((s) => s.filters);
  const summary = useOrdersStore((s) => s.summary);
  const setSource = useOrdersStore((s) => s.setSource);
  const setFilters = useOrdersStore((s) => s.setFilters);
  const resetFilters = useOrdersStore((s) => s.resetFilters);
  const fetchList = useOrdersStore((s) => s.fetchList);
  const fetchSummary = useOrdersStore((s) => s.fetchSummary);
  const cancel = useOrdersStore((s) => s.cancel);

  const handleCancel = async (id: string) => {
    const o = items.find((x) => String(x.id) === id);
    if (!o) return;
    const orderLabel = `#${o.id}`;
    if (!window.confirm(`Cancel order ${orderLabel}? Reserved inventory will be released. This cannot be undone.`)) return;
    try {
      await cancel(o.id);
      fetchSummary().catch(() => undefined);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Failed to cancel order');
    }
  };

  useEffect(() => {
    setSource('admin');
    // Clear any filter (e.g. status='pending') left over from another page
    // before showing the full admin list.
    resetFilters();
    setFilters({ limit: ITEMS_PER_PAGE });
    fetchList();
    fetchSummary();
  }, [setSource, resetFilters, setFilters, fetchList, fetchSummary]);

  const adapted = useMemo<OrderItem[]>(() => items.map(adapt), [items]);

  const totalPages = Math.max(1, Math.ceil(total / (filters.limit ?? ITEMS_PER_PAGE)));
  const currentPage = Math.floor((filters.offset ?? 0) / (filters.limit ?? ITEMS_PER_PAGE)) + 1;

  const goToPage = (page: number) => {
    setFilters({ offset: (page - 1) * (filters.limit ?? ITEMS_PER_PAGE) });
    fetchList();
  };

  const stats: OrderStats = {
    delivered: summary?.delivered ?? 0,
    inTransit: summary?.shipped ?? 0,
    pending: summary?.pending ?? 0,
    processing: summary?.confirmed ?? 0,
    cancelled: summary?.cancelled ?? 0,
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.sectionTitle}>Orders</h2>
          <p className={styles.sectionSubtitle}>Manage and track customer orders</p>
        </div>
        <button className={styles.createBtn} onClick={() => setShowCreateModal(true)}>
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
          Create Order
        </button>
      </div>

      <div className={styles.statsGrid}>
        <OrderStatCard label="Delivered" count={stats.delivered} />
        <OrderStatCard label="In Transit" count={stats.inTransit} />
        <OrderStatCard label="Pending" count={stats.pending} />
        <OrderStatCard label="Processing" count={stats.processing} />
      </div>

      <div className={styles.statsGridRow2}>
        <OrderStatCard label="Cancelled" count={stats.cancelled} />
      </div>

      <AsyncBoundary
        loading={loading && items.length === 0}
        error={error}
        empty={!loading && adapted.length === 0}
        emptyMessage="No orders to display"
        onRetry={() => fetchList()}
      >
        <OrdersTable items={adapted} onCancel={handleCancel} />
        {totalPages > 1 && (
          <OrdersPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
          />
        )}
      </AsyncBoundary>

      <CreateOrderModal open={showCreateModal} onClose={() => setShowCreateModal(false)} />
    </div>
  );
};

export const AdminOrdersPage: React.FC = () => (
  <ProtectedRoute requiredRoles={['admin', 'Sales Rep', 'Customer']}>
    <AdminOrders />
  </ProtectedRoute>
);

export { AdminOrders };
export default AdminOrders;
